/**
 * Reports which slide object is currently selected, as { id, type }.
 *
 * Selection in this app is DOM state, not Redux: use-resizer adds a `.selected` class to
 * the chosen element. Rather than refactor that, this hook observes the slide container and
 * derives the id and object type from the selected node's own classes — the same classes
 * the renderers already set.
 */

import { useCallback, useEffect, useState } from 'react';
import { SLIDE_SELECTION_CLEAR } from './use-resizer';

/** Root class -> animation target type. Text boxes use 'text'/'title' by id prefix. */
const typeForElement = (el) => {
  if (el.classList.contains('smartart')) return 'smartart';
  if (el.classList.contains('wordart')) return 'wordart';
  if (el.classList.contains('shape')) return 'shape';
  if (el.classList.contains('image')) return 'image';
  if (el.classList.contains('text') || el.classList.contains('title')) return 'textbox';
  return null;
};

const readSelection = () => {
  const el = document.querySelector('#slide01 .selected');
  if (!el || !el.id) return null;
  const type = typeForElement(el);
  return type ? { id: el.id, type } : null;
};

export const useSelectedObject = () => {
  const [selected, setSelected] = useState(null);

  const sync = useCallback(() => {
    const next = readSelection();
    // Compare by value so an unchanged selection doesn't re-render the toolbar.
    setSelected((prev) =>
      prev?.id === next?.id && prev?.type === next?.type ? prev : next
    );
  }, []);

  useEffect(() => {
    sync();

    document.addEventListener('click', sync);
    // clearAllSlideSelections dispatches on `window`, not `document` — listening on the
    // wrong target fails silently, so this must match use-resizer's dispatch.
    window.addEventListener(SLIDE_SELECTION_CLEAR, sync);

    // `.selected` is toggled imperatively, so watch the class directly. Observing body
    // rather than #slide01 keeps this working when the toolbar mounts before the slide.
    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      document.removeEventListener('click', sync);
      window.removeEventListener(SLIDE_SELECTION_CLEAR, sync);
      observer.disconnect();
    };
  }, [sync]);

  return selected;
};

export default useSelectedObject;
